import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Button,
  FormControlLabel,
  IconButton,
  ListSubheader,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import classNames from 'classnames';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useCallback, useState } from 'react';
import * as yup from 'yup';
import { NumericInput } from '../../Input/NumericInput';
import {
  DividendEtf,
  DIVIDEND_ETFS,
  DividendFormValues,
} from '../DividendPage.hooks';

const INITIAL_VALUES: DividendFormValues = {
  etfId: 'tiger-us-dividend',
  initialInvestment: 50_000_000,
  monthlyAddition: 500_000,
  years: 20,
  drip: true,
};

const schema = yup.object().shape({
  etfId: yup.string().required('ETF를 선택해 주세요.'),
  initialInvestment: yup
    .number()
    .min(100_000, '초기 투자금은 10만 원 이상이어야 합니다.')
    .max(2_000_000_000, '20억 원 이하로 입력해 주세요.')
    .required('필수 입력 항목입니다.'),
  monthlyAddition: yup
    .number()
    .min(0, '0 이상이어야 합니다.')
    .max(10_000_000, '월 1,000만 원 이하로 입력해 주세요.')
    .required('필수 입력 항목입니다.'),
  years: yup
    .number()
    .min(1, '1년 이상이어야 합니다.')
    .max(40, '40년 이하로 입력해 주세요.')
    .required('필수 입력 항목입니다.'),
});

const KR_ETFS = DIVIDEND_ETFS.filter((etf) => etf.region === 'KR');
const US_ETFS = DIVIDEND_ETFS.filter((etf) => etf.region === 'US');

function FieldTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);

  return (
    <Tooltip open={open} onClose={() => setOpen(false)} title={text} arrow placement="top">
      <IconButton size="small" onClick={() => setOpen((v) => !v)} sx={{ p: 0.25, color: '#9ca3af' }}>
        <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
    </Tooltip>
  );
}

function renderEtfOptions(etfs: DividendEtf[]) {
  return etfs.map((etf) => (
    <MenuItem key={etf.id} value={etf.id}>
      {etf.ticker}
    </MenuItem>
  ));
}

export interface DividendFormProps {
  onSubmit: (values: DividendFormValues) => void;
  className?: string;
}

export function DividendForm({ onSubmit, className }: DividendFormProps) {
  const handleSubmit = useCallback((values: DividendFormValues) => onSubmit(values), [onSubmit]);

  return (
    <Formik initialValues={INITIAL_VALUES} validationSchema={schema} onSubmit={handleSubmit}>
      {({ errors, setFieldValue, values, submitForm }) => (
        <Form className={classNames(className)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Field name="etfId">
              {({ field }: FieldProps) => (
                <div>
                  <label
                    htmlFor={field.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#111827',
                      marginBottom: '8px',
                    }}
                  >
                    ETF 선택
                    <FieldTooltip text="ETF를 선택하면 아래 비교 테이블에서 다른 ETF와 나란히 비교할 수 있습니다." />
                  </label>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    name={field.name}
                    value={field.value}
                    onChange={(e) => setFieldValue(field.name, e.target.value)}
                  >
                    <ListSubheader>국내 상장 ETF (원화 투자, 배당소득세 15.4%)</ListSubheader>
                    {renderEtfOptions(KR_ETFS)}
                    <ListSubheader>미국 직접 투자 ETF (달러 투자, 미국 원천징수세 15%)</ListSubheader>
                    {renderEtfOptions(US_ETFS)}
                  </TextField>
                </div>
              )}
            </Field>
            {errors.etfId && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.etfId}</div>}

            <Field name="initialInvestment">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>초기 투자금 (원)<FieldTooltip text="지금 바로 투자할 수 있는 금액입니다. 현재 보유 자산을 모두 배당 ETF에 넣는 게 아니라, 배당 포트폴리오에 배정할 금액을 입력하세요." /></span>}
                  placeholder="50,000,000"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.initialInvestment && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.initialInvestment}</div>}

            <Field name="monthlyAddition">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>월 추가 납입액 (원)<FieldTooltip text="매달 추가로 ETF를 매수할 금액입니다. 0원 입력 시 초기 투자금만으로 계산합니다." /></span>}
                  placeholder="500,000"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.monthlyAddition && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.monthlyAddition}</div>}

            <Field name="years">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>투자 기간 (년)<FieldTooltip text="배당을 수령하기 시작할 시점까지의 기간입니다. 기간이 길수록 복리 효과가 극적으로 커집니다." /></span>}
                  placeholder="20"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.years && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.years}</div>}

            <FormControlLabel
              control={
                <Switch
                  checked={values.drip}
                  onChange={async (_, checked) => {
                    await Promise.resolve(setFieldValue('drip', checked, false));
                    await submitForm();
                  }}
                  color="primary"
                />
              }
              label={
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  배당금 재투자 (DRIP)
                  <FieldTooltip text="배당금을 받아서 쓰지 않고 다시 ETF를 매수합니다. DRIP ON이면 복리 효과로 장기 자산이 크게 늘어나고, OFF이면 매년 배당금을 실수령액으로 계산합니다." />
                </span>
              }
              sx={{ alignItems: 'flex-start', ml: 0 }}
            />
          </div>

          <div style={{ marginTop: '24px' }}>
            <Button
              variant="contained"
              type="submit"
              fullWidth
              size="large"
              sx={{ background: '#000033', '&:hover': { background: '#001166' } }}
            >
              배당 시뮬레이션 실행하기
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
